import React, { useState, useEffect } from "react";
import { Modal, Button, Card, Alert, Spinner, Form } from "react-bootstrap";
import {
  getTagDefinitions,
  getEntityTags,
  updateEntityTag,
} from "../services/APIClient";

function SchoolFeatureTagsModal({ show, onHide, schoolId, schoolName, userName }) {
  const [tagDefinitions, setTagDefinitions] = useState([]);
  const [schoolTags, setSchoolTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [changedTags, setChangedTags] = useState({});

  useEffect(() => {
    if (show && schoolId) {
      fetchData();
    }
  }, [show, schoolId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch available tag definitions
      const definitions = await getTagDefinitions();
      setTagDefinitions(definitions);

      // Fetch this school's current tag values
      const tags = await getEntityTags('school', schoolId);
      setSchoolTags(tags);

      setChangedTags({});
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(`Failed to load tags: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTagChange = (tagDefinitionId, newValue) => {
    setChangedTags({
      ...changedTags,
      [tagDefinitionId]: newValue,
    });
    setSaveSuccess(false);
  };

  const getCurrentTagValue = (tagDefinitionId) => {
    // Check if tag has been changed locally
    if (changedTags.hasOwnProperty(tagDefinitionId)) {
      return changedTags[tagDefinitionId];
    }

    // Otherwise, get from school tags
    const schoolTag = schoolTags.find(t => t.tag_definition_id === tagDefinitionId);
    return schoolTag?.tag_value || 'OFF';
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      // Update all changed tags
      const updatePromises = Object.entries(changedTags).map(([tagDefinitionId, newValue]) =>
        updateEntityTag('school', schoolId, tagDefinitionId, newValue, userName)
      );

      await Promise.all(updatePromises);

      // Refresh the data after saving
      await fetchData();
      setSaveSuccess(true);

      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Error saving tags:", err);
      setError(`Failed to save changes: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setChangedTags({});
    setError(null);
    setSaveSuccess(false);
    onHide();
  };

  const renderTagControl = (definition) => {
    const currentValue = getCurrentTagValue(definition.id);

    if (definition.data_type === 'boolean') {
      return (
        <Form.Check
          type="switch"
          id={`tag-${definition.id}`}
          label={currentValue === 'ON' ? 'Enabled' : 'Disabled'}
          checked={currentValue === 'ON'}
          onChange={(e) =>
            handleTagChange(definition.id, e.target.checked ? 'ON' : 'OFF')
          }
          disabled={saving}
        />
      );
    }

    if (definition.data_type === 'number') {
      return (
        <Form.Control
          type="number"
          value={currentValue}
          onChange={(e) => handleTagChange(definition.id, e.target.value)}
          disabled={saving}
          placeholder="Enter number"
        />
      );
    }

    // Default to text input for string type
    return (
      <Form.Control
        type="text"
        value={currentValue}
        onChange={(e) => handleTagChange(definition.id, e.target.value)}
        disabled={saving}
        placeholder="Enter value"
      />
    );
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Feature & Tag Management
          <br />
          <small className="text-muted">{schoolName}</small>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {saveSuccess && (
          <Alert variant="success" onClose={() => setSaveSuccess(false)} dismissible>
            ✓ Changes saved successfully
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" className="mb-3">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p>Loading available features...</p>
          </div>
        ) : tagDefinitions.length === 0 ? (
          <Alert variant="info">
            No features available to manage at this time.
          </Alert>
        ) : (
          <div className="tags-container">
            {tagDefinitions.map((definition) => (
              <Card key={definition.id} className="mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="mb-1">{definition.tag_name}</h6>
                      <p className="text-muted small mb-0">
                        {definition.description}
                      </p>
                      <small className="text-secondary">
                        Type: {definition.data_type}
                      </small>
                    </div>
                    <div style={{ minWidth: '150px', marginLeft: '1rem' }}>
                      {renderTagControl(definition)}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={saving}
        >
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveChanges}
          disabled={saving || Object.keys(changedTags).length === 0 || loading}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            `Save Changes (${Object.keys(changedTags).length})`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SchoolFeatureTagsModal;